import { COUNTRY_ECOTAX_TYPE, IOrder } from '@wyz/types';
import {
  Card,
  Col,
  Flex,
  FormatDate,
  FormatDeliveryDate,
  Icon,
  InformationalLegends,
  SoftBadge,
  Spinner,
  Title,
  Tooltip,
} from '@wyz/ui';
import {
  CANCELLED_ORDER_STATUSES,
  DateUtils,
  invariant,
  isEmptyValue,
} from '@wyz/utils';
import { isEmpty, sumBy, uniq } from 'lodash-es';
import * as React from 'react';
import { useParams } from 'react-router';
import { useManageEnums } from '~/common/api';
import { useLocale, useConfig, Trans } from '~/common/context';
import { IUserCustomer } from '~/common/types';
import { useManageRedistributionOrders } from '~/purchase/api';
import {
  CartTotalSection,
  GeneratedOrderItem,
  OrdersDeliveryItem,
  OrdersTyreItem,
} from '~/purchase/components';
import { OrderHelpers } from '~/purchase/helpers';
import { ORDER_STATUS_LABEL_LOOKUP } from '~/purchase/translators';

type Props = {
  order: IOrder;
  customer: IUserCustomer;
};

const DetailView: React.FC<Props> = ({ order, customer }) => {
  const { locale } = useLocale();

  const { id } = useParams();
  const { tenantCode } = useConfig();
  invariant(id);
  const enums = useManageEnums({
    getOrderTypes: { enabled: true },
  });
  const orders = useManageRedistributionOrders({
    getGeneratedOrders: {
      args: { reference: id, customerId: customer.id, tenantCode },
      enabled: true,
    },
  });

  const cancelledOrderStatusesIds = Object.values(
    CANCELLED_ORDER_STATUSES,
  ) as Array<number>;

  return (
    <>
      <Col sm={4}>
        <Card style={{ minHeight: 140 }}>
          <Card.Body className='space-y-2'>
            <Flex>
              <Title heading='lg' weight='bold' className='text-truncate'>
                <Trans i18nKey='app.purchase.orders.table.order_number' />{' '}
                {' : '}
                {order.orderReference}
              </Title>
            </Flex>
            <Flex>
              <SoftBadge
                bg={
                  OrderHelpers.PROCESSED_ORDER_STATUS_COLOR_LOOKUP[
                    order.logisticStatusId
                  ]
                }
                pill
              >
                <Trans
                  i18nKey={ORDER_STATUS_LABEL_LOOKUP[order.logisticStatusId]}
                />
              </SoftBadge>
            </Flex>
            {!isEmptyValue(order.dmsReference) && (
              <Flex>
                <Trans
                  i18nKey='app.purchase.orders.processed.table.dms_reference'
                  values={{
                    reference: order.dmsReference,
                  }}
                />
              </Flex>
            )}
          </Card.Body>
        </Card>
      </Col>
      <Col sm={4}>
        <Card style={{ minHeight: 140 }}>
          <Card.Body className='space-y-2'>
            <InformationalLegends.Container>
              <InformationalLegends.Row>
                <InformationalLegends.Label>
                  <Trans i18nKey='app.purchase.orders.table.order_date' />
                </InformationalLegends.Label>
                <InformationalLegends.Value id='ordered-at'>
                  <FormatDate
                    date={order.orderedAt}
                    format={DateUtils.DATE_FORMAT.DMY_HM}
                    timezone={customer.deliveryInformation.timeZone}
                    locale={locale}
                  />
                </InformationalLegends.Value>
              </InformationalLegends.Row>
              <InformationalLegends.Row>
                <InformationalLegends.Label>
                  <Trans i18nKey='app.purchase.orders.processed.table.validation_date' />
                </InformationalLegends.Label>
                <InformationalLegends.Value id='processed-at'>
                  <FormatDate
                    date={order.validationUtcDate}
                    format={DateUtils.DATE_FORMAT.DMY_HM}
                    timezone={customer.deliveryInformation.timeZone}
                    locale={locale}
                  />
                </InformationalLegends.Value>
              </InformationalLegends.Row>
              <InformationalLegends.Row>
                <InformationalLegends.Label>
                  <Trans i18nKey='app.purchase.orders.table.estimated_delivery_date' />
                </InformationalLegends.Label>
                <InformationalLegends.Value id='delivered-at'>
                  <FormatDeliveryDate
                    orderType={order.type}
                    backorderContent={
                      <Trans i18nKey='app.common.offer_type.backorder' />
                    }
                    date={order.dateDelivered}
                    format={DateUtils.DATE_FORMAT.DMY}
                    timezone={customer.deliveryInformation.timeZone}
                    locale={locale}
                  />
                </InformationalLegends.Value>
              </InformationalLegends.Row>
            </InformationalLegends.Container>
          </Card.Body>
        </Card>
      </Col>
      <Col sm={4}>
        <Card style={{ minHeight: 140 }}>
          <Card.Body className='space-y-2'>
            <InformationalLegends.Container>
              <InformationalLegends.Row>
                <InformationalLegends.Label>
                  <Trans i18nKey='app.purchase.orders.table.supplier' />
                </InformationalLegends.Label>
                <InformationalLegends.Value id='supplier-name'>
                  {order.supplierName}
                </InformationalLegends.Value>
              </InformationalLegends.Row>
              <InformationalLegends.Row>
                <InformationalLegends.Label>
                  <Trans i18nKey='app.purchase.orders.table.type' />
                </InformationalLegends.Label>
                <InformationalLegends.Value id='order-type'>
                  <Tooltip
                    label={
                      order.type
                        ? enums.getOrderTypes.get(order.type)?.label
                        : ''
                    }
                    enabled={Boolean(order.type)}
                  >
                    <SoftBadge bg='primary' pill>
                      {order.type || '-'}
                    </SoftBadge>
                  </Tooltip>
                </InformationalLegends.Value>
              </InformationalLegends.Row>
              <InformationalLegends.Row>
                <InformationalLegends.Label>
                  <Trans i18nKey='app.common.user' />
                </InformationalLegends.Label>
                <InformationalLegends.Value id='orderer-name'>
                  {order.ordererName}
                </InformationalLegends.Value>
              </InformationalLegends.Row>
            </InformationalLegends.Container>
          </Card.Body>
        </Card>
      </Col>
      <Col sm={12} className='mt-3'>
        <Title heading='lg' weight='bold'>
          <Trans i18nKey='app.purchase.orders.detail.title' />
        </Title>
      </Col>
      <Col sm={8} className='mt-3'>
        {order.tyreLines.map((orderItem, index) => {
          return (
            <Card key={index} className='mt-2'>
              <Card.Body>
                <OrdersTyreItem
                  customer={customer}
                  orderItem={
                    order.ecotaxType !== COUNTRY_ECOTAX_TYPE.NOT_DISPLAYED
                      ? {
                          ...orderItem,
                          ecoTaxPrice: order.ecoTaxLines.find(
                            (ecoTaxItem) => ecoTaxItem.ean === orderItem.ean,
                          )?.priceCustomer,
                        }
                      : orderItem
                  }
                  currency={order.currency}
                  ecotaxType={order.ecotaxType}
                />
              </Card.Body>
            </Card>
          );
        })}
        {order.deliveryLines?.map((serviceItem, index) => {
          return (
            <Card key={index} className='mt-2'>
              <Card.Body>
                <OrdersDeliveryItem order={order} serviceItem={serviceItem} />
              </Card.Body>
            </Card>
          );
        })}
        <div className='mt-3'>
          {!orders.getGeneratedOrders.isSettled && <Spinner />}
          {orders.getGeneratedOrders.isSuccess &&
            isEmptyValue(order.dmsReference) &&
            !cancelledOrderStatusesIds.includes(order.logisticStatusId) &&
            !isEmpty(orders.getGeneratedOrders.data.data) && (
              <>
                {orders.getGeneratedOrders.data.data.map((order) => (
                  <GeneratedOrderItem
                    key={order.orderReference}
                    order={order}
                  />
                ))}
              </>
            )}
        </div>
      </Col>
      <Col sm={4} className='mt-3'>
        <>
          <CartTotalSection
            values={{
              referencesCount: uniq(order.tyreLines.map((item) => item.ean))
                .length,
              articlesCount: sumBy(order.tyreLines, 'quantity'),
              servicesCount: sumBy(order.deliveryLines, 'quantity'),
              totalExclTaxProduct: sumBy(
                order.tyreLines,
                'excludedTaxesAmount',
              ),
              totalExclTaxDelivery: sumBy(
                order.deliveryLines,
                'excludedTaxesAmount',
              ),
              totalExclTaxEcoTax: sumBy(
                order.ecoTaxLines,
                'excludedTaxesAmount',
              ),
              taxesAmount: order.taxesAmount,
              totalExclTax: order.excludedTaxesAmount,
              inclTaxTotal: order.includedTaxesAmount,
            }}
            privileges={{
              ecotaxType: order.ecotaxType,
            }}
            mode='ORDER'
            customer={customer}
            currency={order.currency}
          />
          <Card className='mt-2'>
            <Card.Body className='bg-neutral-grey-1'>
              <Flex>
                <span className='text-royal-blue'>
                  <Icon icon='chat-line' size='xs' />
                </span>
                <Title heading='lg' weight='bold' className='mb-3' wrap>
                  <Trans i18nKey='app.common.comment_of_customer' />
                </Title>
              </Flex>
              <div>{order.comment || ''}</div>
            </Card.Body>
          </Card>
          <Card className='mt-2'>
            <Card.Body className='bg-neutral-grey-1'>
              <Flex>
                <span className='text-royal-blue'>
                  <Icon icon='chat-line' size='xs' />
                </span>
                <Title heading='lg' weight='bold' className='mb-3' wrap>
                  <Trans i18nKey='app.purchase.orders.processed.detail.comment' />
                </Title>
              </Flex>
              <div>{order.supplierComment || ''}</div>
            </Card.Body>
          </Card>
        </>
      </Col>
    </>
  );
};

export default DetailView;
